<template>
  <section class="responsibility-badge-holders-tab">
    <ResponsibilityBadgeHoldersTable
      :responsibility-id="responsibilityId"
      :items="items"
      :items-count="totalCount"
      :filter-levels="filterLevels"
      :badge-level-counts="badgeLevelCounts"
      @removeBadge="handleRemoveBadge"
      @filtersChanged="handleFiltersChanged"
      @onActionClick="openForceBadgeModal"
    />
    
    <EmployeeModal
      :showStep="1"
      :dialog="showBadgeDialog"
      @onAction="addForceBadgeData"
      @openEvalAction="openEvalResponse"
      @back="backForceBadge"
      label="Employee"
      :name="responsibilityData?.name"
      :items="badgeEmployeeData"
      :activeCheckpoint="activeCheckPoints"
      :value="[1]"
      :enableEmployeePagination="true"
      :empForceBadgeClass="true"
      :key="employeeModalRenderKey"
      :responsibility="formattedResponsibilityData"
      @removeBadge="handleRemoveBadge"
      :isAssigningBadge="isAssigningBadge"
    />
    
    <RemoveBadgeDialog
      v-model="showRemoveBadgeDialog"
      :badgeName="selectedBadgeForRemoval?.badgeName || selectedBadgeForRemoval?.level || ''"
      :responsibilityName="props.responsibilityData?.name || ''"
      :employeeName="selectedBadgeForRemoval?.employeeName || ''"
      @remove="handleConfirmRemoveBadge"
      @close="handleCloseRemoveBadgeDialog"
    />

    <EvalResponsibilityModal
      v-if="openEvalResponsibility"
      :dialog="openEvalResponsibility"
      :checkpointsData="checkpointsData"
      :evalModalHeaderState="evalModalHeaderState"
      :evalModalEmpState="evalModalEmpState"
      :currentResponsibilityId="responsibilityId"
      @closeModal="openEvalResponsibility = false"
      @updateEvaluationCheckPointData="updateEvaluationCheckPointData"
      @reloadData="updateEvaluationCheckPointData"
      @grantBadge="grantOrForceBadge"
      @removeBadge="removeBadgeByResponsibilityId"
      @reloadBadgeHolderItems="reloadBadgeHolderItems"
      @upgradeBadge="upgradeBadge"
      @showAwardBadge="showAwardBadge"
    />

    <BadgeConfirmationDialog
      :isVisible="showBadgeConfirmationDialog"
      :badgeData="badgeData"
      :badgeType="
        badgeData?.payload?.issued_method == 'given' ? 'Give' : 'Award'
      "
      :responsibilityName="props.responsibilityData?.name || ''"
      :employee="{
        name: `${evalModalEmpState?.firstName} ${evalModalEmpState?.lastName}`,
        avatar: evalModalEmpState?.photoUrl,
      }"
      :level="getBadgeLevel(badgeData?.payload?.level)"
      @close="showBadgeConfirmationDialog = false"
      :currentEmployee="evalModalEmpState"
      @onClick="onClick"
    />

    <AwardBadgeDialog
      :employee="{
        name: `${evalModalEmpState?.firstName}`,
        avatar: evalModalEmpState?.photoUrl,
      }"
      :level="awardBadgeLevel"
      :isVisible="showAwardBadgeModal"
      @skip="showAwardBadgeModal = false"
      @close="showAwardBadgeModal = false"
      @activate="awardBadge"
      :currentEmployee="evalModalEmpState"
      :responsibilityName="props.responsibilityData?.name || ''"
    />
  </section>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, computed, watch } from "vue";
import { getListofResponsibilityBadge, getListofResponsibilityBadgeV2, deleteResponsibilityBadge, createProficiencyBadge, getListActiveCheckpointsEvalV2, getEmployeeBadges } from "@/api";
import ResponsibilityBadgeHoldersTable from "@components/ResponsibilityBadgeHoldersTable/ResponsibilityBadgeHoldersTable.vue";
import RemoveBadgeDialog from "@components/RemoveBadgeDialog/RemoveBadgeDialog.vue";
import EmployeeModal from "@components/EmployeeModal/EmployeeModal.vue";
import EvalResponsibilityModal from "@components/EvalResponsibilityModal/EvalResponsibilityModal.vue";
import BadgeConfirmationDialog from "@components/BadgeConfirmationDialog/BadgeConfirmationDialog.vue";
import AwardBadgeDialog from "@components/AwardBadgeDialog/AwardBadgeDialog.vue";
import { useBadgeHolders } from "@components/ResponsibilityBadgeHoldersTable/composables/useBadgeHolders";
import { useBadgeHoldersViewState } from "@components/ResponsibilityBadgeHoldersTable/composables/useBadgeHoldersViewState";

const { proxy } = getCurrentInstance();

const props = defineProps({
  responsibilityId: {
    type: [Number, String],
    required: true,
  },
  responsibilityData: { 
    type: Object,
    required: false,
  },
  status: {
    type: String,
    required: false,
    validator: (value) => {
      if (!value) return true; // Allow undefined/null
      return ['Current', 'Expired', 'Terminated', 'Removed'].includes(value);
    },
  },
  filterLevels: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['badgeCountsUpdated']);

// Composables
const badgeHolders = useBadgeHolders();
const viewState = useBadgeHoldersViewState();

// State
const items = ref([]);
const totalCount = ref(0);
const showRemoveBadgeDialog = ref(false);
const selectedBadgeForRemoval = ref(null);
const selectedStatusFilters = ref([]);
const selectedLevelFilters = ref([]);
const showBadgeDialog = ref(false);
const badgeEmployeeData = ref([]);
const activeCheckPoints = ref(0);
const employeeModalRenderKey = ref(0);
const isAssigningBadge = ref(false);
const openEvalResponsibility = ref(false);
const checkpointsData = ref([]);
const evalModalHeaderState = ref(null);
const evalModalEmpState = ref(null);
const badgeData = ref(null);
const showBadgeConfirmationDialog = ref(false);
const showAwardBadgeModal = ref(false);
const awardBadgeLevel = ref(null);
const badgeFrameItems = ref([]);

// Badge level counts from responsibilityData
const badgeLevelCounts = computed(() => {
  const counts = {
    apprentice: props.responsibilityData?.badge_apprentice_holder_count || 0,
    professional: props.responsibilityData?.badge_professional_holder_count || 0,
    coach: props.responsibilityData?.badge_coach_holder_count || 0,
    master: props.responsibilityData?.badge_master_holder_count || 0,
  };
  return counts;
});

// Format responsibility data to ensure code is a string for EmployeeModal
const formattedResponsibilityData = computed(() => {
  if (!props.responsibilityData) return null;
  
  const data = { ...props.responsibilityData };
  
  // Ensure code is a string - extract from code_str or convert code number to string
  if (data.code_str) {
    // Extract code from code_str (e.g., "#123 - Name" -> "#123")
    const codeMatch = data.code_str.match(/#\d+/);
    if (codeMatch) {
      data.code = codeMatch[0];
    } else {
      // If no # found, try to extract just the number and add #
      const numMatch = data.code_str.match(/\d+/);
      data.code = numMatch ? `#${numMatch[0]}` : (data.code ? String(data.code).startsWith('#') ? String(data.code) : `#${data.code}` : '');
    }
  } else if (data.code !== undefined && data.code !== null) {
    // Convert code to string format, ensuring it starts with #
    const codeStr = String(data.code);
    data.code = codeStr.startsWith('#') ? codeStr : `#${codeStr}`;
  } else {
    data.code = '';
  }
  
  return data;
});

const getBadgeDotFromStatus = (status) => {
  const statusMap = {
    given: "Given",
    good_standing: "Good Standing",
    obsolete: "Obsolete",
  };
  return statusMap[status] || "Good Standing";
};

const getBadgeIconFromRemoveMethod = (removeMethod) => {
  const iconMap = {
    upgraded: "upgraded",
    forced: "removed",
    removed: "removed",
  };
  return iconMap[removeMethod] || null;
};

const transformBadgeHolders = (results) => {
  if (!results || !Array.isArray(results)) return [];
  
  return results.map((item) => {
    const fullName = `${item.issued_to?.first_name || ''} ${item.issued_to?.last_name || ''}`.trim();
    const badgeView = (!item.is_current && !!item.remove_method) || (item.is_current && !item.removed_by) 
      ? "Editable" 
      : "ReadOnly";
    
    return {
      id: item.id,
      is_current: item.is_current,
      user: {
        id: item.issued_to?.id,
        name: fullName,
        email: item.issued_to?.email || '',
      },
      employee: {
        id: item.issued_to?.id,
        name: fullName,
        avatar: item.issued_to?.avatar,
        title: item.issued_to?.title || null,
      },
      userStatus: item.issued_to?.current_status || "active",
      employeeStatus: item.issued_to?.current_status || "active",
      accountHover: true,
      badge: {
        level: item.level || "Novice",
        dot: getBadgeDotFromStatus(item.status),
        view: badgeView,
        expiration_date: item.expiration_date || null,
        has_expiration_if_obsolete: !!item.expires_when_obsolete || item.awarded_expiration_obsolete > 0,
        upgraded: !item.is_current && !!item.remove_method,
        upgradedVariantIcon: getBadgeIconFromRemoveMethod(item.remove_method),
        unread: 0,
        hideChatterIcon: true,
      },
      assignedBy: item.issued_by ? {
        name: item.issued_by.name || "",
        picture: item.issued_by.picture || "",
        userType: "user",
        displayDate: item.issued_on || null,
      } : null,
      removedBy: item.removed_by ? {
        name: item.removed_by.name || "",
        picture: item.removed_by.picture || "",
        userType: "user",
        displayDate: item.removed_on || null,
      } : null,
      reason: item.remove_reason && typeof item.remove_reason === "string" && item.remove_reason.trim() !== "" ? item.remove_reason : "",
      status: item.status,
      remove_method: item.remove_method,
      issued_for: item.issued_for,
    };
  });
};

const getBadges = async () => {
  try {
    proxy.$loader.show();
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const statusToUse = selectedStatusFilters.value.length > 0 
      ? selectedStatusFilters.value 
      : (props.status || null);
    const levelToUse = selectedLevelFilters.value.length > 0 
      ? selectedLevelFilters.value 
      : (props.filterLevels && props.filterLevels.length > 0 
          ? props.filterLevels.map(level => typeof level === 'string' ? getBadgeLevelByLabel(level) : level)
          : null);
    
    const limit = badgeHolders.paginationDefaultParams.limit;
    const offset = badgeHolders.paginationDefaultParams.offset;
    const search = viewState.state.filters.searchText || null;
    const ordering = badgeHolders.orderingData.field || null;
    
    // Serialize params manually to handle arrays correctly (status=Current&status=Terminated)
    const queryParts = [];
    
    if (statusToUse !== null && statusToUse !== undefined) {
      const statusArray = Array.isArray(statusToUse) ? statusToUse : [statusToUse];
      statusArray.forEach((status) => {
        queryParts.push(`status=${encodeURIComponent(status)}`);
      });
    }
    
    if (levelToUse !== null && levelToUse !== undefined && levelToUse.length > 0) {
      const levelArray = Array.isArray(levelToUse) ? levelToUse : [levelToUse];
      levelArray.forEach((level) => {
        queryParts.push(`level=${encodeURIComponent(level)}`);
      });
    }
    
    if (limit !== null && limit !== undefined) {
      queryParts.push(`limit=${encodeURIComponent(limit)}`);
    }
    
    if (offset !== null && offset !== undefined) {
      queryParts.push(`offset=${encodeURIComponent(offset)}`);
    }
    
    if (search !== null && search !== undefined && search !== '') {
      queryParts.push(`employee_name=${encodeURIComponent(search)}`);
    }
    
    if (ordering !== null && ordering !== undefined) {
      queryParts.push(`ordering=${encodeURIComponent(ordering)}`);
    }
    
    // Build params object with serialized query string
    const params = queryParts.length > 0 ? { _query: queryParts.join('&') } : {};
    
    const response = await getListofResponsibilityBadge(
      accountId, 
      props.responsibilityId, 
      'responsibility', 
      params
    );
    
    if (response.status === 200) {
      const transformedData = transformBadgeHolders(response.data.results || []);
      items.value = transformedData;
      totalCount.value = response.data.count || 0;
      badgeHolders.badgeHoldersCount.value = totalCount.value;
    } else {
      console.error("Error fetching badges:", response.data);
    }
  } catch (error) {
    console.error("Error fetching badges:", error);
  }
  finally {
    proxy.$loader.hide();
  }
};

const handleFiltersChanged = (filters) => {
  if (Array.isArray(filters)) {
    selectedStatusFilters.value = filters;
    selectedLevelFilters.value = [];
  } else if (filters && typeof filters === 'object') {
    selectedStatusFilters.value = filters.status || [];
    selectedLevelFilters.value = filters.level || [];
  } else {
    selectedStatusFilters.value = [];
    selectedLevelFilters.value = [];
  }
  badgeHolders.setPaginationDataOffset(0);
  getBadges();
};

const handleRemoveBadge = (item) => {
  if (!item || !item.id) {
    console.error("Invalid badge data:", item);
    proxy.$toast?.error("Invalid badge data");
    return;
  }

  // Extract badge level and name
  const badgeLevel = item.badge?.level || item.level || "";
  const badgeLevelName = getBadgeLevelName(badgeLevel);
  
  selectedBadgeForRemoval.value = {
    id: item.id,
    badgeName: badgeLevelName,
    level: badgeLevel,
    employeeName: item.employee?.name || item.employeeName || '',
  };
  
  showRemoveBadgeDialog.value = true;
};

function getBadgeLevelName(level) {
  if (typeof level === 'string') {
    // Capitalize first letter
    return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
  }
  const levelMap = {
    1: "Apprentice",
    2: "Professional",
    3: "Coach",
    4: "Master",
  };
  return levelMap[level] || "Apprentice";
}

const handleConfirmRemoveBadge = async ({ reason: removeReason }) => {
  if (!selectedBadgeForRemoval.value || !selectedBadgeForRemoval.value.id || !removeReason?.trim()) {
    proxy.$toast?.error("Please provide a reason for removing the badge");
    return;
  }

  try {
    proxy.$loader.setAppLoading(true);
    const payload = {
      badge: selectedBadgeForRemoval.value.id,
      method: "removed",
      reason: removeReason.trim(),
    };
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    
    await deleteResponsibilityBadge(accountId, selectedBadgeForRemoval.value.id, payload);
    
    await getBadges();
    emit('badgeCountsUpdated');
    
    proxy.$toast?.success("Badge removed successfully");
    
    showRemoveBadgeDialog.value = false;
    selectedBadgeForRemoval.value = null;
  } catch (error) {
    console.error("Error removing badge:", error);
    proxy.$toast?.error("Error removing badge");
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const handleCloseRemoveBadgeDialog = () => {
  showRemoveBadgeDialog.value = false;
  selectedBadgeForRemoval.value = null;
};

const openForceBadgeModal = () => {
  const data = props.responsibilityData;
  if (data) {
    activeCheckPoints.value = 
      (data.active_apprentice_checkpoints || 0) +
      (data.active_coach_checkpoints || 0) +
      (data.active_master_checkpoints || 0) +
      (data.active_professional_checkpoints || 0);
    
    badgeEmployeeData.value = [];
    showBadgeDialog.value = true;
  }
};

const backForceBadge = () => {
  showBadgeDialog.value = false;
};

const addForceBadgeData = async (res) => {
  try {
    isAssigningBadge.value = true;
    proxy.$loader.setAppLoading(true);
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    
    const payload = {
      employee: res.employee.id,
      responsibility: props.responsibilityId,
      level: res.radioVal,
      forced: true,
    };
    
    await createProficiencyBadge(accountId, payload);
    
    await getBadges();
    emit('badgeCountsUpdated');
    
    showBadgeDialog.value = false;
    updateEmployeeList();
  } catch (error) {
    console.error("Error adding badge:", error);
    if (error.response && error.response.data && error.response.data.non_field_errors) {
      showBadgeDialog.value = false;
    }
  } finally {
    isAssigningBadge.value = false;
    proxy.$loader.setAppLoading(false);
  }
};

const getProficiencyLevelNameFromNumber = (el) => {
  if (el.proficiency_level === 1) return "Apprentice";
  if (el.proficiency_level === 2) return "Professional";
  if (el.proficiency_level === 3) return "Coach";
  if (el.proficiency_level === 4) return "Master";
  return "Unknown";
};

const updateChangeEvaluationStatus = (data) => {
  if (data.evaluation_status == "not_applicable") return "Not Applicable";
  if (data.evaluation_status == "passed") return "Passed";
  if (data.evaluation_status == "failed") return "Failed";
  if (data.evaluation_status == "reset") return "Reset Pending Evaluation";
  return "Pending Evaluation";
};

const loadListActiveCheckpointsEvalV2 = async (data) => {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const responsibilityData = props.responsibilityData;
    const limit = responsibilityData?.active_checkpoints_count || activeCheckPoints.value || 0;
    const offset = 0;
    
    const response = await getListActiveCheckpointsEvalV2(
      accountId,
      props.responsibilityId,
      data.employee.id,
      limit,
      offset
    );
    
    if (response && response.data) {
      checkpointsData.value = response.data?.results.map((el) => {
        const newEl = {};
        newEl.id = el.checkpoint;
        newEl.checkpoint_id = el.checkpoint;
        newEl.level = el.proficiency_level;
        newEl.level_name = getProficiencyLevelNameFromNumber(el);
        newEl.criteria = el.checkpoint_criteria;
        newEl.instructions = el.checkpoint_instructions;
        newEl.status = el.status;
        newEl.evaluation_status = el.status;
        newEl.change_evaluation_status = updateChangeEvaluationStatus({ evaluation_status: el.status });
        newEl.failed_by = el.evaluated_by?.name || null;
        newEl.failed_on = el.evaluated_on || null;
        newEl.failed_reason = el.reason || null;
        newEl.reset_by = el.reset_by || null;
        newEl.reset_on = el.reset_on || "";
        newEl.reset_reason = "";
        newEl.updated_on = el.evaluated_on || null;
        newEl.updated_by = el.evaluated_by ? {
          ...el.evaluated_by,
          picture: el.evaluator_avatar,
        } : null;
        newEl.avatar = el.evaluator_avatar || null;
        return newEl;
      }) || [];
    }
  } catch (error) {
    console.error("Error loading checkpoints:", error);
    checkpointsData.value = [];
  }
};

const openEvalResponse = async (data) => {
  try {
    showBadgeDialog.value = false;
    proxy.$loader.setAppLoading(true);
    
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    
    const extractCode = (str) => {
      if (!str) return null;
      const match = str.match(/#\d+/);
      return match ? match[0] : null;
    };
    
    const responsibilityData = props.responsibilityData;
    evalModalHeaderState.value = {
      responsibilityCode: extractCode(responsibilityData?.code_str) || 
        (responsibilityData?.code ? `#${responsibilityData.code}` : ''),
      responsibilityName: responsibilityData?.name || '',
      roleCode: "#245",
      roleName: "Implement CAPEX initiatives",
      positionCode: "#4567",
      positionName: "General Manager",
    };
    
    let employeeBadges = [];
    try {
      const badgesResponse = await getEmployeeBadges(accountId, data.employee.id);
      if (badgesResponse && badgesResponse.data) {
        employeeBadges = badgesResponse.data.results || [];
      }
    } catch (error) {
      console.error("Error fetching employee badges:", error);
    }
    
    const employeeCurrentBadge = employeeBadges.find(
      (badge) => 
        badge.is_current && 
        badge.issued_for?.code === responsibilityData?.code_str
    );
    
    const emp = data.employee;
    const empId = emp.id;
    const firstName = emp.firstName || emp.first_name || '';
    const lastName = emp.lastName || emp.last_name || '';
    const fullName = emp.fullName || `${firstName} ${lastName}`.trim();
    
    evalModalEmpState.value = {
      id: empId,
      firstName: firstName,
      lastName: lastName,
      position: emp.title || emp.position || '',
      photoUrl: emp.avatar || emp.photoUrl || '',
      availability: 1,
      isAllocated: false,
      access: emp.access || {},
      badgeList: employeeBadges.map(badge => ({
        id: badge.id,
        issued_to: badge.issued_to,
        is_current: badge.is_current,
      })),
      currentEmployeeBadge: employeeCurrentBadge ? {
        id: employeeCurrentBadge.id,
        level: employeeCurrentBadge.level,
        status: employeeCurrentBadge.status,
      } : null,
      user: emp.user || null,
    };
    
    await loadListActiveCheckpointsEvalV2({ employee: { id: empId } });
    
    openEvalResponsibility.value = true;
  } catch (error) {
    console.error("Error opening evaluation modal:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const updateEvaluationCheckPointData = async () => {
  if (evalModalEmpState.value) {
    await loadListActiveCheckpointsEvalV2({ employee: evalModalEmpState.value });
  }
};

const getResponsibilityBadges = async () => {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const response = await getListofResponsibilityBadgeV2(
      accountId,
      props.responsibilityId,
      "responsibility",
      null
    );
    
    if (response && response.data && response.data.results) {
      const dataResponsibilitiesBadge = response.data.results;
      badgeFrameItems.value = dataResponsibilitiesBadge
        .filter((badge) => badge.issued_to)
        .map((badge) => {
          let statusName = "";
          if (badge.status === "good_standing") {
            statusName = "Good Standing";
          } else if (badge.status === "forced" || badge.status === "given") {
            statusName = "Given";
          } else {
            statusName = "Obsolete";
          }
          return {
            id: badge.id,
            proficiencylevel: badge.level,
            avatar: badge.issued_to?.avatar || "",
            firstName: badge.issued_to?.first_name || "",
            lastName: badge.issued_to?.last_name || "",
            dot: statusName,
            empId: badge.issued_to?.id || null,
            is_current: badge.is_current,
            expires_when_obsolete: badge.expires_when_obsolete,
            removed_by: badge.removed_by,
          };
        })
        .filter((item) => item !== undefined);
    }
  } catch (error) {
    console.error("Error fetching responsibility badges:", error);
  }
};

const grantOrForceBadge = (badgeDataParam) => {
  openEvalResponsibility.value = false;
  showBadgeConfirmationDialog.value = true;
  badgeData.value = badgeDataParam;
};

const onClick = async (isNotify = false) => {
  showBadgeConfirmationDialog.value = false;
  openEvalResponsibility.value = false;
  proxy.$loader.show();
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    
    if (badgeData.value?.currentBadge) {
      await deleteResponsibilityBadge(accountId, badgeData.value.currentBadge, {
        badge: badgeData.value.currentBadge,
        method: "upgraded",
        reason: "",
      });
    }
    
    await createProficiencyBadge(accountId, {
      ...badgeData.value.payload,
      notify: isNotify,
    });
    
    await getBadges();
    await getResponsibilityBadges();
    emit('badgeCountsUpdated');
    updateEmployeeList();
    
    if (evalModalEmpState.value) {
      evalModalEmpState.value = {
        ...evalModalEmpState.value,
        badgeList: badgeFrameItems.value,
      };
    }
  } catch (error) {
    console.error("Error in onClick:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
    proxy.$loader.hide();
  }
};

const removeBadgeByResponsibilityId = async (badgeDataParam) => {
  try {
    if (badgeDataParam?.id) {
      proxy.$loader.setAppLoading(true);
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      await deleteResponsibilityBadge(accountId, badgeDataParam.id, {
        badge: badgeDataParam.id,
        method: "removed",
        reason: "",
      });
      openEvalResponsibility.value = false;
      
      await getBadges();
      await getResponsiblityBadges();
      emit('badgeCountsUpdated');
      
      updateEmployeeList();
      
      if (evalModalEmpState.value) {
        evalModalEmpState.value = {
          ...evalModalEmpState.value,
          badgeList: badgeFrameItems.value,
        };
      }
    }
  } catch (error) {
    console.error("Error removing badge:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const reloadBadgeHolderItems = async () => {
  proxy.$loader.show();
  await getBadges();
  proxy.$loader.hide();
};

const getBadgeLevelByLabel = (level) => {
  if (!level || typeof level !== "string") {
    return 0;
  }
  switch (level.toLowerCase()) {
    case "novice":
      return 0;
    case "apprentice":
      return 1;
    case "professional":
      return 2;
    case "coach":
      return 3;
    case "master":
      return 4;
    default:
      return 0;
  }
};

const getBadgeLevel = (level) => {
  switch (level) {
    case 1:
      return "Apprentice";
    case 2:
      return "Professional";
    case 3:
      return "Coach";
    case 4:
      return "Master";
    default:
      return "Unknown";
  }
};

const upgradeBadge = async (upgradeData) => {
  try {
    badgeData.value = { payload: upgradeData?.payload?.newBadge };
    await onClick(false);
  } catch (error) {
    console.error("Error upgrading badge:", error);
  }
};

const showAwardBadge = (obj) => {
  openEvalResponsibility.value = false;
  showAwardBadgeModal.value = true;
  awardBadgeLevel.value = obj?.level;
};

const awardBadge = async (isNotify = false) => {
  showAwardBadgeModal.value = false;
  proxy.$loader.show();
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const data = {
      employee: evalModalEmpState.value.id,
      responsibility: props.responsibilityId,
      level: getBadgeLevelByLabel(awardBadgeLevel.value),
      given: false,
      issued_method: "awarded",
      notify: isNotify,
    };
    
    await createProficiencyBadge(accountId, data);
    
    showAwardBadgeModal.value = false;
    await getBadges();
    await getResponsiblityBadges();
    emit('badgeCountsUpdated');
    
    updateEmployeeList();
    
    if (evalModalEmpState.value) {
      evalModalEmpState.value = {
        ...evalModalEmpState.value,
        badgeList: badgeFrameItems.value,
      };
    }
  } catch (error) {
    console.error("Error awarding badge:", error);
  } finally {
    proxy.$loader.hide();
  }
};

const updateEmployeeList = () => {
  badgeEmployeeData.value = [];
  employeeModalRenderKey.value++;
};

watch(
  () => badgeHolders.paginationDefaultParams.offset,
  () => {
    getBadges();
  }
);

watch(
  () => badgeHolders.paginationDefaultParams.limit,
  () => {
    badgeHolders.setPaginationDataOffset(0);
  }
);

watch(
  () => viewState.state.filters.searchText,
  () => {
    getBadges();
  }
);

watch(
  () => badgeHolders.orderingData.field,
  () => {
    getBadges();
  }
);

onMounted(async () => {
  await getBadges();
});
</script>
